import type { FC, SVGProps } from 'react';

import { IconButton, Typography } from '@mui/material';
import {
  BillingIcon,
  InformationIcon,
  LogoIcon,
  LogoutDashboardIcon,
  NavButton,
  QuestionIcon,
  UsersBoldIcon,
} from '@truvhq/ui';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { SandboxSwitch } from 'components/SandboxSwitch/SandboxSwitch';
import { useAssistantEnabled } from 'features/onboarding/lib/useAssistantEnabled';
import { useOnboardingDialogStore } from 'features/onboarding/lib/useOnboardingDialogStore';
import { TenantCompanies } from 'features/tenancy/ui/TenantCompanies';
import { useGetTenantCompanies } from 'hooks/company';
import { useAuth } from 'shared/lib/auth/useAuth';
import { useSandboxContext } from 'shared/lib/sandbox/useSandboxContext';
import { useTenancyContext } from 'shared/lib/tenancy/useTenancyContext';
import DoubleChevronIcon from 'shared/ui/icons/double-chevron.svg?react';
import { urls } from 'shared/urls';

import type { Item } from './types';
import { isItemHidden } from './utils/isItemHidden';
import { isRoutePart } from './utils/isRoutePart';

const iconProps: SVGProps<SVGSVGElement> = {
  width: 20,
  height: 20,
};

// TEMPORARY — design review build only, do not merge. The drawer normally carries the two pages
// a phone is built for; this lists the same destinations the desktop rail does, so a designer can
// walk the whole app on a device. Permissions are left off deliberately, so nothing is hidden on
// the review account, and the four `desktopOnly` route guards are lifted alongside it so Reporting,
// Performance and Team performance actually open. The external links — docs, support — stay out,
// as does the assistant row below.
const navigationItems: Item[] = [
  { title: urls.reporting.title, href: urls.reporting.url() },
  { title: urls.emulator.title, href: urls.emulator.url() },
  { title: urls.allUsers.title, href: urls.allUsers.url() },
  { title: urls.orders.title, href: urls.orders.url(), icon: <UsersBoldIcon {...iconProps} /> },
  { title: urls.tasks.title, href: urls.tasks.url() },
  { title: urls.coverage.title, href: urls.coverage.url() },
  { title: urls.performance.title, href: urls.performance.url() },
  { title: urls.managers.title, href: urls.managers.url() },
  { title: urls.branding.title, href: urls.branding.url() },
  { title: 'Order settings', href: urls.customSettings.url() },
  { title: urls.templates.title, href: urls.templates.url() },
  { title: urls.fieldsCustomization.title, href: urls.fieldsCustomization.url() },
  { title: urls.keys.title, href: urls.keys.url() },
  { title: urls.platforms.title, href: urls.platforms.url() },
  { title: urls.logs.title, href: urls.logs.url() },
  { title: urls.webhooks.title, href: urls.webhooks.url() },
  { title: urls.subscriptions.title, href: urls.subscriptions.url(), icon: <BillingIcon {...iconProps} /> },
  { title: urls.invoices.title, href: urls.invoices.url() },
  { title: urls.creditLedger.title, href: urls.creditLedger.url() },
  { title: urls.billingUsage.title, href: urls.billingUsage.url() },
  { title: urls.monthlyReport.title, href: urls.monthlyReport.url() },
  { title: urls.company.title, href: urls.company.url() },
  { title: urls.team.title, href: urls.team.parameterUrl?.() ?? urls.team.url() },
  { title: urls.profile.title, href: urls.profile.url() },
  { title: urls.notifications.title, href: urls.notifications.url() },
];

const supportItem: Item = {
  title: urls.supportTickets.title,
  permission: urls.supportTickets.permission,
  icon: <InformationIcon stroke="currentColor" {...iconProps} />,
  customAction() {
    window.open(urls.supportTickets.url(), '_blank');
  },
};

type MobileNavProps = {
  onClose: VoidFunction;
  onLogout: VoidFunction;
};

export const MobileNav: FC<MobileNavProps> = ({ onClose, onLogout }) => {
  const { pathname } = useLocation();
  const { permissions } = useAuth();
  const { isSandboxVisible } = useSandboxContext();
  const { data: tenantCompanies } = useGetTenantCompanies();
  const { tenantCompanyId, setTenantCompanyId } = useTenancyContext();
  const assistantEnabled = useAssistantEnabled();
  const openAssistant = useOnboardingDialogStore((state) => state.open);

  // The drawer is the assistant's door on a phone: the desktop's floating pill would sit on top of
  // whatever the page pins to its bottom edge (CL-6904). It takes the support slot rather than
  // adding a fourth row — the assistant answers the same questions, and without leaving the tab.
  const assistantItem: Item = {
    title: 'Ask Truv AI',
    icon: <QuestionIcon {...iconProps} />,
    customAction: openAssistant,
  };
  const items = [...navigationItems, assistantEnabled ? assistantItem : supportItem];

  return (
    <div className="relative flex h-full w-[300px] max-w-[85vw] flex-col bg-grey100" data-testid="mobile-nav">
      <div className="flex h-full flex-col overflow-y-auto">
        <div className="flex items-center px-6 pt-5 pb-8">
          <LogoIcon className="h-5 w-auto text-white" />
        </div>

        <div className="flex flex-col gap-2 px-4">
          {items.map((item) =>
            isItemHidden(item, permissions) ? null : (
              <NavButton
                key={item.title}
                active={isRoutePart(pathname, item.href)}
                className="justify-start !h-10"
                component={item.href ? RouterLink : undefined}
                icon={item.icon}
                title={item.title}
                // Closed here rather than left to the route change: every route renders its own
                // layout, so navigating unmounts this drawer and mounts another one, which would
                // come up open — two overlays across one commit, and the second one's backdrop
                // can outlive its transition.
                onClick={() => {
                  item.customAction?.();
                  onClose();
                }}
                {...(item.href && { to: { pathname: item.href, state: { internal: true } } })}
              />
            ),
          )}
        </div>

        <div className="mt-auto flex flex-col gap-4 px-4 pt-4">
          <NavButton
            className="h-12 justify-start"
            icon={<LogoutDashboardIcon className="[&_path]:stroke-white" {...iconProps} />}
            title="Log out"
            onClick={onLogout}
          />
          <div className="rounded-lg border border-grey70 p-4">
            <Typography className="text-white" variant="h3">
              Mobile dashboard is limited
            </Typography>
            <Typography className="mt-1 text-grey50" variant="body2">
              Switch to desktop for the full experience
            </Typography>
          </div>
        </div>

        <div className="mt-4 flex flex-col bg-grey90">
          {tenantCompanies && tenantCompanies.length > 1 && (
            <div className="flex h-[52px] flex-col justify-center">
              <TenantCompanies
                disableTooltip
                selectedCompanyId={tenantCompanyId}
                tenantCompanies={tenantCompanies}
                onSelectCompany={setTenantCompanyId}
              />
            </div>
          )}
          {isSandboxVisible && (
            <div className="flex h-[54px] flex-col justify-center border-white/10 border-t-2">
              <div className="px-4">
                <SandboxSwitch invert />
              </div>
            </div>
          )}
        </div>
      </div>

      <IconButton
        aria-label="Close navigation"
        className="-translate-y-1/2 absolute top-[30px] right-0 h-8 w-8 translate-x-1/2 bg-grey80 p-0 text-white hover:bg-grey70"
        data-testid="mobile-nav-close"
        onClick={onClose}
      >
        <DoubleChevronIcon className="h-5 w-5 rotate-180" />
      </IconButton>
    </div>
  );
};
